import { Link, Package, Plus, Unlink, X } from "lucide-react";
import { GripVertical, Search } from "lucide-react";
import { basisOptions, currencyOptions } from "./getInvoiceColumns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@radix-ui/react-tooltip";
import { getChargeCodes } from '@/redux/Booking/actions';
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { ACCOUNTING_DIVISION } from "@/utils/accountingBookingUtils";
import {
    buildArApSyncPayload,
    executeArApSync,
    isArApSyncSuccess,
    SYNC_CHARGE_KIND,
} from "@/utils/accountingSyncUtils";

const ChargesSection = ({
    formData,
    setFormData,
    setShowLinkChargesDialog,
    accountingDivision = ACCOUNTING_DIVISION.EXPORT,
    syncChargeKind = SYNC_CHARGE_KIND.AR,
}) => {
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");
    const username = authUser?.firstName + ' ' + authUser?.lastName || authUser?.username || "SYSTEM";


    const addLine = () => {
        setFormData({
            ...formData,
            lines: [
                ...formData.lines,
                {
                    line_no: formData.lines.length + 1,
                    chargeCode: '',
                    charge_code_id: '',
                    description: '',
                    quantity: 1,
                    unit_price: 0,
                    amount: 0,
                    tax_rate: 0,
                    tax_amount: 0,
                    total_amount: 0,
                    source_charge_id: 0,
                    comments: '',
                }
            ]
        });
    };


    const handleChange = (index, field, value) => {
        const newLines = [...formData.lines];

        if (typeof field === "object") {
            // ✅ MULTI FIELD UPDATE
            newLines[index] = {
                ...newLines[index],
                ...field
            };
        } else {
            // ✅ SINGLE FIELD UPDATE
            newLines[index] = {
                ...newLines[index],
                [field]: value
            };
        }

        // Recalculate
        const qty = parseFloat(newLines[index].quantity) || 0;
        const price = parseFloat(newLines[index].unit_price) || 0;
        const taxRate = parseFloat(newLines[index].tax_rate) || 0;

        newLines[index].amount = qty * price;
        newLines[index].tax_amount = newLines[index].amount * (taxRate / 100);
        newLines[index].total_amount =
            newLines[index].amount + newLines[index].tax_amount;
        newLines[index].tax_rate = taxRate;

        setFormData({ ...formData, lines: newLines });
        console.log("Updated Line:", newLines[index]);
    };
    const deleteLine = (index) => {
        const newLines = formData.lines.filter((_, i) => i !== index);
        setFormData({ ...formData, lines: newLines });
    };
    // ✅ Totals
    const subTotal = formData.lines.reduce((sum, l) => sum + l.amount, 0);
    const totalTax = formData.lines.reduce((sum, l) => sum + l.tax_amount, 0);
    const grandTotal = formData.lines.reduce((sum, l) => sum + l.total_amount, 0);


    const handleSync = async (isSync, data, syncType = "update") => {
        try {
            if (!isSync) return true;

            const division = data?.division || accountingDivision;
            const syncPayload = buildArApSyncPayload({
                document: data,
                chargeKind: syncChargeKind,
                syncType,
                isSync: true,
                createdBy: username,
                division,
            });

            const bookingResponse = await executeArApSync(syncPayload, division);

            if (isArApSyncSuccess(bookingResponse)) {
                return true;
            }
            toast.error("Sync failed");
            return false;
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong");
            return false;
        }
    };


    const delinkLine = async (index) => {
        const oldLine = formData.lines[index];

        if (!oldLine?.source_charge_id || oldLine?.source_charge_id <= 0) {
            toast.error("This line is not linked to any shipment charge.");
            return;
        }

        // ✅ STEP 1: Send ORIGINAL data to API
        const updatedData = {
            ...formData,
            lines: [oldLine], // ✅ original line with ID
        };

        await handleSync(true, updatedData, "delete");

        // ✅ STEP 2: Update UI AFTER sync
        const newLines = [...formData.lines];
        newLines[index] = {
            ...newLines[index],
            source_charge_id: undefined,
        };

        setFormData({ ...formData, lines: newLines });

        toast.info("Charge delinked from shipment.");
    };



    const linkLine = async (index) => {
        const oldLine = formData.lines[index];

        // ✅ CONDITION: run only if source_charge_id > 0
        if (!oldLine?.source_charge_id || oldLine.source_charge_id <= 0) {
            toast.error("This line is not linked to any shipment charge.");
            return;
        }

        // ✅ Prepare data for API
        const updatedLine = {
            ...oldLine,
            source_charge_id: oldLine.source_charge_id, // keep same or modify if needed
        };

        const updatedData = {
            ...formData,
            lines: [updatedLine],
        };

        await handleSync(true, updatedData, "update");

        // ✅ Update UI AFTER sync
        const newLines = [...formData.lines];
        newLines[index] = updatedLine;

        setFormData({ ...formData, lines: newLines });

        toast.info("Charge linked to shipment.");
    };





    return (
        <div className=" mt-4 rounded-lg border border-l-4 border-l-amber-500 overflow-hidden border-slate-200 dark:border-slate-800">

            {/* HEADER */}
            <div className="flex items-center gap-3 px-4 py-3 border-b bg-slate-50 dark:bg-slate-900">
                <span className="text-sm font-semibold uppercase">Charges</span>
            </div>

            {/* LINES */}
            <div className="p-4 space-y-3">
                {formData.lines.map((line, index) => (
                    <ChargeRow
                        key={index}
                        index={index}
                        line={line}
                        onChange={handleChange}
                        onDelete={deleteLine}
                        formData={formData}
                        linkLine={linkLine}
                        delinkLine={delinkLine}
                        chargeCodes={formData.charge_codes}
                    />
                ))}


                {/* ADD BUTTON */}
                <div className="mt-3 flex gap-2">
                    <div
                        onClick={addLine}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50  cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 rounded-md px-3 text-xs bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white border-0"
                    >
                        <Plus size={14} /> Add More Charges
                    </div>

                    {/* {formData.booking_id && (
                  <div
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLinkChargesDialog(true)}
                    className="text-xs gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/20"
                  >
                    <Package className="h-3.5 w-3.5" /> Link Shipment Charges
                  </div>
                )} */}
                </div>


                {/* TOTAL BOX */}
                <div className="flex justify-end mt-4">
                    <div className="w-96 space-y-2 p-5 rounded-lg border shadow-md bg-primary/5 border-border border-l-4 border-l-primary">

                        {/* Sub Total */}
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-muted-foreground">
                                Sub Total:
                            </span>
                            <span className="text-sm font-semibold tabular-nums font-mono">
                                {subTotal.toFixed(2)}
                            </span>
                        </div>

                        {/* VAT */}
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-muted-foreground">
                                VAT:
                            </span>
                            <span className="text-sm font-semibold tabular-nums font-mono">
                                {totalTax.toFixed(2)}
                            </span>
                        </div>

                        {/* Total */}
                        <div className="flex justify-between items-center border-t-2 pt-2 border-primary/20">
                            <span className="text-lg font-bold">Total:</span>

                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground font-medium">
                                    USD
                                </span>
                                <span className="text-lg font-bold tabular-nums font-mono text-primary">
                                    {grandTotal.toFixed(2)}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChargesSection;







const ChargeRow = ({
    index,
    line,
    onChange,
    onDelete,
    linkLine,
    delinkLine
}) => {
    const [chargeCodes, setChargeCodes] = useState([]);
    const [loadingCharges, setLoadingCharges] = useState(false);
    const [chargeCodeOpenIndex, setChargeCodeOpenIndex] = useState(null);
    const dispatch = useDispatch();
    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList,
        commodityGroupList, containerTypeList, operatorList, moveTypeList, currency_list, charges_basis_list, fileManagerList } = useSelector((state) => state.common);



    const handleOpen = async (open, index) => {
        setChargeCodeOpenIndex(open ? index : null);

        if (open && chargeCodes.length === 0) {
            setLoadingCharges(true);
            try {
                const data = await dispatch(getChargeCodes("o", 0));
                if (data && Array.isArray(data)) {
                    setChargeCodes(data);
                } else {
                    setChargeCodes([]);
                }
            } catch (error) {
                console.error("Error fetching charge codes:", error);
                setChargeCodes([]);
            } finally {
                setLoadingCharges(false);
            }
        }
    };

    const handleSearch = async (value) => {
        setLoadingCharges(true);
        try {
            const data = await dispatch(getChargeCodes(value || "o", 0));
            if (data && Array.isArray(data)) {
                setChargeCodes(data);
            } else {
                setChargeCodes([]);
            }
        } catch (error) {
            console.error("Error searching charge codes:", error);
            setChargeCodes([]);
        } finally {
            setLoadingCharges(false);
        }
    };

    const handleChargeCodeSelect = (code, description, id) => {
        // Update both fields in one go
        onChange(index, {
            charge_code_id: String(id),
            description: description,
            chargeCode: code,
        });


        // Close the popover
        setChargeCodeOpenIndex(null);
    };

    const handleChange = (key, value) => {
        onChange(index, key, value);
    };

    // Get display text for charge code button
    const getChargeCodeDisplay = () => {
        if (line?.chargeCode) {
            return line.chargeCode;
        }
        return "Select Code";
    };

    useEffect(() => {
        if (line?.charge_code_id && !line?.chargeCode) {
            fetchChargeCode();
        }
    }, [line?.charge_code_id]);

    const fetchChargeCode = async () => {
        if (!line?.charge_code_id) return;

        try {
            const data = await dispatch(getChargeCodes(line?.charge_code_id, 1));

            if (data && Array.isArray(data) && data.length > 0) {
                const item = data[0];

                const code = item?.descriptioncode || "";
                const description = item?.chargesdescription || "";
                const id = item?.descriptionId || "";

                onChange(index, {
                    charge_code_id: String(id),
                    description: description,
                    chargeCode: code,
                });
            }
        } catch (err) {
            console.error("Error fetching charge code:", err);
        }
    };

    return (
        <div
            className={cn(
                "rounded-lg border p-4 space-y-3 animate-fade-in transition-colors",
                index % 2 === 0 ? "bg-card/60" : "bg-muted/20"
            )}
            style={{ animationDelay: `${index * 50}ms`, animationFillMode: "both" }}
        >
            {/* HEADER */}
            <div className="flex items-center gap-3 flex-wrap">
                {/* Drag */}
                <div className="cursor-grab active:cursor-grabbing shrink-0">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                </div>

                {/* Line No */}
                <span
                    className={cn(
                        "inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold shrink-0",
                        "bg-[#10274c] text-primary-foreground"
                    )}
                >
                    {index + 1}
                </span>

                {/* DP */}
                <div className="flex items-center gap-1.5 shrink-0">
                    <input
                        type="checkbox"
                        checked={line.is_dp || false}
                        onChange={(e) => handleChange("is_dp", e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300"
                    />
                    <span className="text-xs text-muted-foreground">DP</span>
                </div>

                {/* Charge Code */}
                <Popover
                    open={chargeCodeOpenIndex === index}
                    onOpenChange={(open) => handleOpen(open, index)}
                >
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            className="h-8 text-xs w-28 shrink-0 justify-between px-2"
                            type="button"
                        >
                            <span className="truncate">
                                {getChargeCodeDisplay()}
                            </span>
                            <Search className="ml-1 h-3 w-3 opacity-50" />
                        </Button>
                    </PopoverTrigger>

                    <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                            <CommandInput
                                placeholder="Search charge code..."
                                className="h-9"
                                onValueChange={(val) => {
                                    handleSearch(val);
                                }}
                            />

                            <CommandList>
                                {loadingCharges && (
                                    <div className="p-2 text-xs text-center">Loading...</div>
                                )}

                                {!loadingCharges && chargeCodes.length === 0 && (
                                    <CommandEmpty>No charge code found.</CommandEmpty>
                                )}

                                <CommandGroup>
                                    {chargeCodes?.map((c, i) => {
                                        // Handle different data structures
                                        let code = '';
                                        let name = '';
                                        let id = '';

                                        if (typeof c === 'object') {
                                            code = c.descriptioncode || c.code || c.charge_code || c[0] || '';
                                            name = c.chargesdescription || c.description || c.name || c[1] || '';
                                            id = c.descriptionId || c[2] || '';
                                        } else {
                                            code = c;
                                            name = '';
                                            id = c;
                                        }

                                        return (
                                            <CommandItem
                                                key={i}
                                                value={`${code} ${name}`}
                                                onSelect={() => {
                                                    handleChargeCodeSelect(code, name, id);
                                                }}
                                                className="text-xs cursor-pointer"
                                            >
                                                <span className="font-medium">{code}</span>
                                                {name && (
                                                    <span className="ml-1 text-muted-foreground">
                                                        - {name}
                                                    </span>
                                                )}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>

                {/* Description */}
                <input
                    className="h-8 text-xs flex-1 min-w-[150px] border rounded-md px-2"
                    placeholder="Description"
                    value={line.description || ''}
                    onChange={(e) => handleChange("description", e.target.value)}
                />

                {!!line?.source_charge_id && (
                    <TooltipProvider>
                        <div className="flex items-center gap-1">
                            {/* Link Icon */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex items-center text-blue-500">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 text-orange-500 hover:text-orange-700"
                                            onClick={() => linkLine(index)}
                                        >
                                            <Link className="h-3.5 w-3.5" />
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">Linked to shipment charge</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Unlink Button */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-orange-500 hover:text-orange-700"
                                        onClick={() => delinkLine(index)}
                                    >
                                        <Unlink className="h-3.5 w-3.5" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">Delink from shipment (make estimated)</p>
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </TooltipProvider>
                )}

                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive shrink-0"
                    onClick={() => onDelete(index)}
                >
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* FINANCIAL ROW */}
            <div className="flex flex-wrap items-end gap-3">
                {/* Currency */}
                <div className="flex-1 min-w-[80px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        Currency
                    </label>
                    <select
                        value={line?.currency || 'USD'}
                        onChange={(e) => handleChange("currency", e.target.value)}
                        className="h-8 text-xs w-full border rounded-md px-2 bg-background"
                    >
                        <option value="USD">USD</option>
                        {currency_list?.map((c, i) => (
                            <option key={i} value={c.currencycode}>{c.currencycode}</option>
                        ))}
                    </select>
                </div>

                {/* ROE */}
                <div className="flex-1 min-w-[70px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        ROE
                    </label>
                    <input
                        type="number"
                        value={line?.roe || 1}
                        min={0}
                        step="0.01"
                        onChange={(e) =>
                            handleChange("roe", parseFloat(e.target.value) || 0)
                        }
                        className="h-8 text-xs w-full border rounded-md px-2"
                    />
                </div>

                {/* Basis */}

                <div className="flex-1 min-w-[90px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        Basis
                    </label>
                    <select
                        value={line?.Basis || 'Per Shipment'}
                        onChange={(e) => handleChange("Basis", e.target.value)}
                        className="h-8 text-xs w-full border rounded-md px-2"
                    >
                        <option value="">Select</option>

                        {charges_basis_list?.map((b, i) => (
                            <option key={i} value={b.chargebasis}>{b.chargebasis}</option>
                        ))}
                    </select>
                </div>

                {/* Qty */}
                <div className="flex-1 min-w-[60px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        Qty
                    </label>
                    <input
                        type="number"
                        value={line?.quantity || 1}
                        min={0}
                        step="0.01"
                        onChange={(e) =>
                            handleChange("quantity", parseFloat(e.target.value) || 0)
                        }
                        className="h-8 text-xs w-full border rounded-md px-2"
                    />
                </div>

                {/* Rate */}
                <div className="flex-1 min-w-[80px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        Rate
                    </label>
                    <input
                        type="number"
                        value={line?.unit_price || 0}
                        min={0}
                        step="0.01"
                        onChange={(e) =>
                            handleChange("unit_price", parseFloat(e.target.value) || 0)
                        }
                        className="h-8 text-xs w-full border rounded-md px-2"
                    />
                </div>

                {/* Amount */}
                <div className="flex-1 min-w-[100px] inline-flex items-center justify-center rounded-full px-3 py-1 text-xs h-8 bg-muted">
                    Amt: {((line?.unit_price || 0) * (line?.quantity || 0)).toFixed(2)}
                </div>

                {/* VAT % */}
                <div className="flex-1 min-w-[80px] space-y-0.5">
                    <label className="text-[10px] text-muted-foreground uppercase">
                        VAT%
                    </label>
                    <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={line?.tax_rate || 0}
                        onChange={(e) =>
                            handleChange("tax_rate", parseFloat(e.target.value) || 0)
                        }
                        className="h-8 text-xs w-full border rounded-md px-2"
                    />
                </div>

                {/* VAT */}
                <div className="flex-1 min-w-[100px] inline-flex items-center justify-center rounded-full px-3 py-1 text-xs h-8 bg-muted">
                    VAT: {(line?.tax_amount || 0).toFixed(2)}
                </div>

                {/* Total */}
                <div className="flex-1 min-w-[110px] inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-bold h-8 bg-primary/10 text-primary">
                    Total: {(line?.total_amount || 0).toFixed(2)}
                </div>
            </div>

            {/* COMMENTS */}
            <div>
                <label className="text-[10px] text-muted-foreground uppercase ml-1">
                    Comments
                </label>
                <input
                    value={line?.comments || ''}
                    onChange={(e) => handleChange("comments", e.target.value)}
                    className="h-8 text-xs w-full border rounded-md px-2"
                    placeholder="Add comments..."
                />
            </div>
        </div>
    );
};